import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/components/ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi there! Welcome to FIGO Digital Services. How can we help you today?",
      sender: 'bot',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showForm, setShowForm] = useState(true);

  const whatsappUrl = 'https://wa.me/2347032914477?text=Hello%20FIGO%20Digital%20Services!%20I%20would%20like%20to%20inquire%20about%20your%20services.';

  const quickReplies = [
    "Tell me about your services",
    "Get a quote",
    "Schedule a consultation",
    "View portfolio"
  ];

  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: messageText,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = getBotResponse(messageText);
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();

    if (msg.includes('service')) {
      return "We offer Software Development, AI Automation, Digital Transformation, UI/UX Design, and more! Would you like to know more about any specific service?";
    } else if (msg.includes('quote') || msg.includes('price')) {
      return "I'd be happy to help you get a quote! Please share your project details or email us at info@figodigitalservice.com";
    } else if (msg.includes('consultation') || msg.includes('meeting')) {
      return "Great! You can schedule a free consultation by visiting our Contact page or calling +234 703 291 4477";
    } else if (msg.includes('portfolio') || msg.includes('work')) {
      return "Check out the solutions we build! We serve businesses across Nigeria and globally. Visit our Portfolio page to see what we can do for you.";
    } else {
      return "Thanks for reaching out! Our team will get back to you shortly. You can also email us at info@figodigitalservice.com for immediate assistance.";
    }
  };

  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (userName.trim() && userEmail.trim()) {
      setShowForm(false);
      const welcomeMsg = {
        id: messages.length + 1,
        text: `Thanks ${userName}! How can we assist you today?`,
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([...messages, welcomeMsg]);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring', stiffness: 200 }}
        className="chat-widget-wrapper"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="chat-toggle-btn"
        >
          {isOpen ? '✕' : '💬'}
          {!isOpen && <span className="chat-online-dot"></span>}
        </button>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="chat-window"
          >
            {/* Header */}
            <div className="chat-header">
              <div className="chat-header-inner">
                <div className="chat-header-left">
                  <div className="chat-header-avatar">
                    <img src="/figo-logo9.png" alt="FIGO" />
                  </div>
                  <div>
                    <h3 className="chat-header-title">FIGO Support</h3>
                    <p className="chat-header-status">
                      <span className="chat-status-dot"></span>
                      Online
                    </p>
                  </div>
                </div>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chat-whatsapp-link"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            </div>

            {/* Messages Area */}
            <div className="chat-messages">
              {/* Welcome Form */}
              {showForm && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="chat-welcome-form"
                >
                  <p>Please introduce yourself to get started:</p>
                  <form onSubmit={handleFormSubmit}>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      required
                      className="chat-form-input"
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      required
                      className="chat-form-input"
                    />
                    <button type="submit" className="chat-form-submit">
                      Start Chat
                    </button>
                  </form>
                </motion.div>
              )}

              {/* Messages */}
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`chat-message-row ${message.sender}`}
                >
                  <div className={`chat-message-bubble ${message.sender}`}>
                    <p className="chat-message-text">{message.text}</p>
                    <p className="chat-message-time">{message.time}</p>
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="chat-typing"
                >
                  <div className="chat-typing-bubble">
                    <span className="chat-typing-dot"></span>
                    <span className="chat-typing-dot"></span>
                    <span className="chat-typing-dot"></span>
                  </div>
                </motion.div>
              )}

              {/* Quick Replies */}
              {!showForm && messages.length < 5 && (
                <div className="chat-quick-replies">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="chat-quick-reply-btn"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input Area */}
            {!showForm && (
              <div className="chat-input-area">
                <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="chat-input-form">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="chat-input"
                  />
                  <button type="submit" className="chat-send-btn">
                    ➤
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
